import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { usePageContent } from "@/hooks/usePageContent";
import { trackEvent } from "@/lib/track";
import { Icon } from "@/lib/icon";
import { Button } from "@/components/ui/button";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";
import { useLead } from "@/components/public/LeadDialog";

export default function NotFound() {
  const { pathname } = useLocation();
  const { openLead } = useLead();
  const c = usePageContent("/404");
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/public/homepage").then((r) => setSettings(r.data.settings)).catch(() => {});
  }, []);

  // Keep broken URLs out of the index while still serving a helpful page.
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    const prev = el ? el.getAttribute("content") : null;
    if (!el) { el = document.createElement("meta"); el.setAttribute("name", "robots"); document.head.appendChild(el); }
    el.setAttribute("content", "noindex, follow");
    document.title = "Page not found — CoworkZen";
    return () => { if (prev != null) el.setAttribute("content", prev); };
  }, []);

  // Record the 404 for the Broken Links report.
  useEffect(() => {
    trackEvent("not_found", { path: pathname, referrer: document.referrer || "" });
  }, [pathname]);

  if (!c) return <div className="min-h-screen bg-white" />;

  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid="notfound-page">
      <Header phone={settings.phone} />

      <div className="relative bg-gradient-to-b from-[#eef3ff] to-white border-b border-slate-100">
        {/* Soft 404 badge tucked in the corner */}
        <span data-testid="notfound-badge"
          className="absolute top-5 right-5 sm:top-6 sm:right-8 inline-flex items-center gap-1.5 rounded-full bg-white/70 backdrop-blur border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-400 tracking-wide">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> 404
        </span>
        <div className={`container-cz py-16 sm:py-20 ${c.image ? "grid lg:grid-cols-2 gap-10 items-center" : "max-w-3xl"}`}>
          <div>
            {c.eyebrow && <p className="eyebrow mb-3">{c.eyebrow}</p>}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight" data-testid="notfound-title">{c.title}</h1>
            {c.subtitle && <p className="text-lg text-slate-600 mt-5">{c.subtitle}</p>}
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/"><Button variant="outline" className="rounded-full h-12 px-7 border-slate-300" data-testid="notfound-home">Back to Home</Button></Link>
              {c.cta_label && (
                <Button onClick={() => openLead("tour")} className="rounded-full h-12 px-7 gap-2" data-testid="notfound-cta">
                  {c.cta_label} <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          {c.image && (
            <div className="rounded-3xl overflow-hidden shadow-cz aspect-[4/3]">
              <img src={c.image} alt="" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </div>

      {(c.links || []).length > 0 && (
        <div className="container-cz py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 flex-1" data-testid="notfound-links">
          {c.links.map((l, i) => (
            <Link key={i} to={l.href || "/"} data-testid={`notfound-link-${i}`}
              className="card-cz p-6 group hover:-translate-y-0.5 hover:shadow-cz transition-all">
              <span className="w-11 h-11 rounded-xl bg-sky-50 text-primary flex items-center justify-center mb-4"><Icon name={l.icon} className="w-5 h-5" /></span>
              <h3 className="font-display text-lg font-bold text-slate-900 group-hover:text-primary transition-colors">{l.title}</h3>
              {l.desc && <p className="text-sm text-slate-500 mt-2 leading-relaxed">{l.desc}</p>}
            </Link>
          ))}
        </div>
      )}

      {c.cta_title && (
        <div className="bg-slate-50 border-t border-slate-100">
          <div className="container-cz py-14">
            <div className="card-cz p-8 sm:p-10 flex flex-col items-start gap-4 max-w-3xl">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{c.cta_title}</h2>
              {c.cta_subtitle && <p className="text-slate-600">{c.cta_subtitle}</p>}
              <div className="flex flex-wrap gap-3 mt-2">
                <Button onClick={() => openLead("tour")} className="rounded-full h-12 px-7" data-testid="notfound-cta-tour">Book a Tour</Button>
                <Button onClick={() => openLead("quote")} variant="outline" className="rounded-full h-12 px-7 border-slate-300" data-testid="notfound-cta-quote">Get a Quote</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer settings={settings} />
    </div>
  );
}
